import * as React from 'react';

import type { PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { CtaBlock } from 'stile-shared/src/components/2020/blocks/CtaBlock';
import { HeroBlock } from 'stile-shared/src/components/2020/blocks/HeroBlock';
import { P, Section, Text } from 'stile-shared/src/components/2020/common/Primitives';
import { Center } from 'stile-shared/src/components/2020/layout/Center';
import { Column } from 'stile-shared/src/components/2020/layout/Column';

import { getPageData } from 'utils/getPageData';

import { theme } from 'styles/theme';

import { PageLayout } from 'templates/2020/PageLayout';

import { NextPageBlock } from 'components/blocks/NextPageBlock';

export { Head } from 'templates/2020/Head';

function HighImpactTeachingStrategiesPage(props: PageProps) {
  const { hero } = getPageData(props.location.pathname);

  return (
    <PageLayout {...props}>
      <HeroBlock
        {...hero}
        image={
          <StaticImage
            loading="eager"
            aspectRatio={2.37}
            src="../../../../stile-shared/assets/images/high-impact-teaching-strategies/cover_high_impact_teaching_strategies.png"
            alt="A class in session with the Stile app presented on a wall projection"
          />
        }
      />

      <Section>
        <Center intrinsic>
          <Column space={theme.space.l}>
            <P>
              For schools that are working to implement some or all of these High Impact Teaching
              Strategies (HITS), Stile naturally helps support their adoption.
            </P>

            <Column space={theme.space.xxxs}>
              <Text as="h2" semiBold>
                Setting goals
              </Text>
              <P>
                Every lesson begins with 1 to 3 clearly articulated learning intentions and success
                criteria.
              </P>
            </Column>

            <Column space={theme.space.xxxs}>
              <Text as="h2" semiBold>
                Structuring lessons
              </Text>
              <P>
                Stile lessons are broken up into clearly marked sections to ensure clarity and
                direction for students – whether they be a video, a set of questions, a practical
                activity, or a metacognitive exercise.
              </P>
            </Column>

            <Column space={theme.space.xxxs}>
              <Text as="h2" semiBold>
                Explicit teaching
              </Text>
              <P>
                Stile is a true blended learning tool, made for the teacher to use at the front of
                the classroom. Teachers are provided with “teaching notes”, which encourage them to
                explicitly teach new concepts and actively engage with their students as they work.
              </P>
            </Column>

            <Column space={theme.space.xxxs}>
              <Text as="h2" semiBold>
                Worked examples
              </Text>
              <P>
                Every single question in Stile has fully worked model answers and, where relevant,
                worked examples.
              </P>
            </Column>

            <Column space={theme.space.xxxs}>
              <Text as="h2" semiBold>
                Multiple exposures
              </Text>
              <P>
                Each new concept is clearly and succinctly explained via text. It’s then reiterated
                through videos and simulations, further explored with careful questioning, and
                finally reinforced by encouraging students to connect new concepts with the old.
              </P>
            </Column>

            <Column space={theme.space.xxxs}>
              <Text as="h2" semiBold>
                Questioning
              </Text>
              <P>
                Questioning is at the heart of Stile. Every lesson contains a range of formative
                questions, from multiple-choice questions to open-ended investigations. These
                questions are carefully crafted by our in-house team of teachers to either promote
                learning, identify misconceptions or elicit understanding of a key concept.
              </P>
            </Column>

            <Column space={theme.space.xxxs}>
              <Text as="h2" semiBold>
                Feedback
              </Text>
              <P>
                Teachers can see all of their students’ work, all the time. They can quickly review
                their progress and provide students with timely written or spoken feedback, right at
                their point of need.
              </P>
            </Column>

            <Column space={theme.space.xxxs}>
              <Text as="h2" semiBold>
                Metacognitive strategies
              </Text>
              <P>
                Every lesson concludes with one of the Harvard visible thinking routines, where
                students are asked to stop and reflect on their learning for the lesson, sharing
                their thinking with the teacher and, optionally, the whole class.
              </P>
            </Column>

            <Column space={theme.space.xxxs}>
              <Text as="h2" semiBold>
                Differentiated teaching
              </Text>
              <P>
                All of Stile’s lessons are carefully scaffolded from lower- to higher-order
                thinking. As students progress through a lesson they are challenged, finding their
                zone of proximal development. In addition, teachers can easily edit Stile’s lessons
                and assign tailored lessons to different students depending on their ability level.
              </P>
            </Column>
          </Column>
        </Center>
      </Section>

      <NextPageBlock path="/why-choose-stile/stem/" />

      <CtaBlock />
    </PageLayout>
  );
}

export default HighImpactTeachingStrategiesPage;
